import { useState, useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import '../styles/richtext-editor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

type EditorMode = 'visual' | 'code';

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start typing…',
}: RichTextEditorProps) {
  const [mode, setMode] = useState<EditorMode>('visual');
  const [codeValue, setCodeValue] = useState(value || '');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange(html);
    },
  });

  // Sync external value changes into editor (e.g. initial load)
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentHtml = editor.getHTML();
      // Only update if the content actually differs to avoid cursor jumps
      if (currentHtml !== value && !(value === '' && currentHtml === '<p></p>')) {
        editor.commands.setContent(value || '');
      }
    }
  }, [value, editor]);

  // When switching modes, sync content
  const handleModeSwitch = useCallback(
    (newMode: EditorMode) => {
      if (newMode === mode) return;

      if (newMode === 'code' && editor) {
        // Switching to code: grab HTML from editor
        setCodeValue(editor.getHTML());
      } else if (newMode === 'visual' && editor) {
        // Switching to visual: push code into editor
        editor.commands.setContent(codeValue);
        onChange(codeValue);
      }

      setMode(newMode);
    },
    [mode, editor, codeValue, onChange],
  );

  const handleCodeChange = useCallback(
    (newCode: string) => {
      setCodeValue(newCode);
      onChange(newCode);
    },
    [onChange],
  );

  // ─── Toolbar actions ──────────────────────────────────────────
  const addLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl || 'https://');
    if (url === null) return; // cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Image URL');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="rte-container">
      {/* Mode tabs */}
      <div className="rte-mode-tabs">
        <button
          type="button"
          className={`rte-mode-tab ${mode === 'visual' ? 'active' : ''}`}
          onClick={() => handleModeSwitch('visual')}
        >
          Visual
        </button>
        <button
          type="button"
          className={`rte-mode-tab ${mode === 'code' ? 'active' : ''}`}
          onClick={() => handleModeSwitch('code')}
        >
          Code
        </button>
      </div>

      {mode === 'visual' ? (
        <>
          {/* Toolbar */}
          <div className="rte-toolbar">
            <div className="rte-toolbar-group">
              <button
                type="button"
                className={`rte-btn ${editor.isActive('bold') ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleBold().run()}
                title="Bold"
              >
                <strong>B</strong>
              </button>
              <button
                type="button"
                className={`rte-btn ${editor.isActive('italic') ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                title="Italic"
              >
                <em>I</em>
              </button>
              <button
                type="button"
                className={`rte-btn ${editor.isActive('underline') ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                title="Underline"
              >
                <u>U</u>
              </button>
              <button
                type="button"
                className={`rte-btn ${editor.isActive('strike') ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleStrike().run()}
                title="Strikethrough"
              >
                <s>S</s>
              </button>
            </div>

            <span className="rte-toolbar-divider" />

            <div className="rte-toolbar-group">
              <button
                type="button"
                className={`rte-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                title="Heading 1"
              >
                H1
              </button>
              <button
                type="button"
                className={`rte-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                title="Heading 2"
              >
                H2
              </button>
              <button
                type="button"
                className={`rte-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                title="Heading 3"
              >
                H3
              </button>
            </div>

            <span className="rte-toolbar-divider" />

            <div className="rte-toolbar-group">
              <button
                type="button"
                className={`rte-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                title="Bullet list"
              >
                • List
              </button>
              <button
                type="button"
                className={`rte-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                title="Numbered list"
              >
                1. List
              </button>
            </div>

            <span className="rte-toolbar-divider" />

            <div className="rte-toolbar-group">
              <button
                type="button"
                className={`rte-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                title="Align left"
              >
                ≡←
              </button>
              <button
                type="button"
                className={`rte-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                title="Align center"
              >
                ≡↔
              </button>
              <button
                type="button"
                className={`rte-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                title="Align right"
              >
                ≡→
              </button>
            </div>

            <span className="rte-toolbar-divider" />

            <div className="rte-toolbar-group">
              <button
                type="button"
                className={`rte-btn ${editor.isActive('link') ? 'active' : ''}`}
                onClick={addLink}
                title="Insert link"
              >
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
              </button>
              <button
                type="button"
                className="rte-btn"
                onClick={addImage}
                title="Insert image"
              >
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              </button>
            </div>

            <span className="rte-toolbar-divider" />

            <div className="rte-toolbar-group">
              <button
                type="button"
                className={`rte-btn ${editor.isActive('blockquote') ? 'active' : ''}`}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                title="Blockquote"
              >
                ❝
              </button>
              <button
                type="button"
                className="rte-btn"
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                title="Horizontal rule"
              >
                ―
              </button>
            </div>
          </div>

          {/* Editor content */}
          <div className="rte-editor-wrapper">
            <EditorContent editor={editor} />
          </div>
        </>
      ) : (
        /* Code mode */
        <div className="rte-code-wrapper">
          <textarea
            className="rte-code-textarea"
            value={codeValue}
            onChange={(e) => handleCodeChange(e.target.value)}
            spellCheck={false}
          />
        </div>
      )}
    </div>
  );
}
